import { useState } from 'react';
import { useAuth } from '../../auth/authContext';
import { useDeskEngine } from '../deskContext';
import type { DeskEvent } from '../events';
import { UnsyncedModal } from './UnsyncedModal';

interface PendingSignOut {
  scope: 'local' | 'global';
  events: DeskEvent[];
  /** The events belong to the desk tab, not this one. */
  heldByOtherTab: boolean;
}

/** Sign-out that is blocked behind the unsynced modal when this DEVICE
 * holds desk events NamibiaGo hasn't confirmed — including when they are
 * held by the check-in desk in another tab (read-only peek of the vault).
 * `scope: 'global'` is "Sign out all devices". */
export function useGuardedSignOut() {
  const engine = useDeskEngine();
  const { supabase } = useAuth();
  const [pending, setPending] = useState<PendingSignOut | null>(null);

  const doSignOut = async (scope: 'local' | 'global') => {
    await supabase.auth.signOut({ scope });
  };

  const request = async (scope: 'local' | 'global') => {
    let events: DeskEvent[];
    try {
      events = await engine.unsyncedEventsOnDevice();
    } catch {
      // Can't tell — be safe and ask.
      events = engine.unsyncedEvents();
      if (events.length === 0) {
        setPending({ scope, events, heldByOtherTab: true });
        return;
      }
    }
    if (events.length === 0) {
      void doSignOut(scope);
      return;
    }
    setPending({ scope, events, heldByOtherTab: !engine.getSnapshot().leader });
  };

  let modal = null;
  if (pending?.heldByOtherTab) {
    // A follower tab can't discard what the desk tab holds. Signing out here
    // signs every tab out; the desk tab then keeps the events behind its own
    // "signed out with unsynced desk events" dialog.
    modal = (
      <UnsyncedModal
        title="The check-in desk has events that haven't synced"
        body="They are held by the check-in desk open in another tab of this browser. Stay signed in so they can sync. If you sign out anyway, the desk tab keeps them and asks what to do."
        events={pending.events}
        primary={{ label: 'Stay signed in', onClick: () => setPending(null) }}
        discardLabel="Sign out anyway"
        confirmLabel="Yes — sign out (the desk tab keeps them)"
        onDiscard={async () => {
          const { scope } = pending;
          setPending(null);
          await doSignOut(scope);
        }}
      />
    );
  } else if (pending) {
    modal = (
      <UnsyncedModal
        title="Desk events haven't synced yet"
        body="Signing out now would delete these from this device before NamibiaGo has them. Stay signed in and they will sync as soon as there is a connection."
        events={pending.events}
        primary={{ label: 'Stay signed in', onClick: () => setPending(null) }}
        discardLabel="Discard and sign out"
        onDiscard={async () => {
          const { scope } = pending;
          setPending(null);
          await engine.wipe();
          await doSignOut(scope);
        }}
      />
    );
  }

  return { request: (scope: 'local' | 'global') => void request(scope), modal };
}
